/**
 * Dev21 — content model + resolver.
 *
 * The template is fully data-driven: every section reads from a single
 * `Dev21Content` object. `resolveContent()` builds that object from the
 * generic `InvitationData` (and its `customData.wedding` extension when
 * present), falling back to the rich demo content below so the template
 * always renders a complete, polished page — even with sparse input.
 */

import type { InvitationData } from '../types';

// ── Stock imagery ──────────────────────────────────────────────────────────
// Curated Unsplash photo IDs (stable, hot-linkable). `img()` builds a sized,
// auto-formatted URL so callers never hard-code query strings.
function img(id: string, w = 1400): string {
  return `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;
}

const STOCK = {
  hero: img('1519741497674-611481863552', 2000),
  partnerA: img('1488426862026-3ee34a7d66df', 900),
  partnerB: img('1507003211169-0a1dd7228f2d', 900),
  story: [
    img('1529636798458-92182e662485'),
    img('1511285560929-80b456fea0bc'),
    img('1469371670807-013ccf25f16a'),
  ],
  gallery: [
    img('1606216794074-735e91aa2c92'),
    img('1519225421980-715cb0215aed'),
    img('1465495976277-4387d4b0b4c6'),
    img('1583939003579-730e3918a45a'),
    img('1522673607200-164d1b6ce486'),
    img('1537633552985-df8429e8048b'),
  ],
  videoPoster: img('1532712938310-34cb3982ef74', 1600),
  events: [
    img('1464366400600-7168b8af9bc3'),
    img('1519741497674-611481863552'),
    img('1530103862676-de8c9debad1d'),
  ],
} as const;

/** Well-known, stable sample MP4 — used as the default teaser clip. */
const DEMO_VIDEO =
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4';

// ── Content shape ──────────────────────────────────────────────────────────

export interface Partner {
  name: string;
  role: string;
  about: string;
  photo: string;
  family?: string;
}

export interface Milestone {
  year: string;
  title: string;
  description: string;
  image?: string;
}

export interface GalleryImage {
  src: string;
  caption?: string;
  /** `tall` items span two rows in the masonry grid. */
  span: 'tall' | 'wide' | 'normal';
}

export interface FamilyMember {
  name: string;
  relation: string;
}

export interface FamilyGroup {
  side: string;
  title: string;
  members: FamilyMember[];
}

export interface ScheduleEvent {
  title: string;
  date: string;
  time: string;
  venue: string;
  description: string;
  image?: string;
}

export interface SocialLink {
  label: string;
  href: string;
}

export interface Dev21Content {
  announcement: string;
  hero: {
    kicker: string;
    title: string;
    subtitle: string;
    host: string;
    dateLabel: string;
    venueShort: string;
    background: string;
  };
  couple: {
    label: string;
    title: string;
    intro: string;
    partners: Partner[];
  };
  story: {
    label: string;
    title: string;
    milestones: Milestone[];
  };
  gallery: {
    label: string;
    title: string;
    images: GalleryImage[];
  };
  video: {
    label: string;
    title: string;
    description: string;
    src: string;
    poster: string;
  };
  family: {
    label: string;
    title: string;
    groups: FamilyGroup[];
  };
  schedule: {
    label: string;
    title: string;
    events: ScheduleEvent[];
  };
  venue: {
    label: string;
    title: string;
    name: string;
    address: string;
    /** Free-text query for the Google Maps embed + directions link. */
    mapQuery: string;
  };
  rsvp: {
    label: string;
    title: string;
    description: string;
    deadline: string;
    /** Optional external RSVP link; when absent the inline form is shown. */
    externalLink?: string;
  };
  footer: {
    message: string;
    signature: string;
    socials: SocialLink[];
  };
}

// ── Helpers ────────────────────────────────────────────────────────────────

const hasText = (v?: string | null): v is string =>
  typeof v === 'string' && v.trim().length > 0;

function formatLong(iso?: string): string {
  if (!hasText(iso)) return 'Date to be announced';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function formatTime(iso?: string, fallback = ''): string {
  if (!hasText(iso)) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

/** Split an "A & B" event name into two display names. */
function splitNames(eventName?: string): [string, string] {
  if (!hasText(eventName)) return ['Aarav', 'Diya'];
  const parts = eventName.split(/\s*(?:&|and|\+|×)\s*/i).filter(Boolean);
  if (parts.length >= 2) return [parts[0].trim(), parts[1].trim()];
  return [eventName.trim(), ''];
}

// ── Demo content ───────────────────────────────────────────────────────────
// Used wholesale when the template is previewed, and field-by-field as the
// fallback for any value the live invitation does not supply.

const DEMO: Dev21Content = {
  announcement: '✨ Our forever begins — you are warmly invited',
  hero: {
    kicker: 'Together with their families',
    title: 'Aarav & Diya',
    subtitle: 'are getting married',
    host: 'The Mehra & Kapoor Families',
    dateLabel: 'Saturday, 14 February 2026',
    venueShort: 'The Leela Palace, Udaipur',
    background: STOCK.hero,
  },
  couple: {
    label: 'The Couple',
    title: 'Two stories, one beginning',
    intro:
      'A quiet hello over coffee became a thousand conversations, countless adventures, and one unshakeable promise. Here is a little about the two of us.',
    partners: [
      {
        name: 'Aarav Mehra',
        role: 'The Groom',
        about:
          'An architect with a weakness for old bookshops, hill stations and Diya’s terrible jokes.',
        photo: STOCK.partnerB,
        family: 'Son of Mr. Rajiv & Mrs. Anita Mehra',
      },
      {
        name: 'Diya Kapoor',
        role: 'The Bride',
        about:
          'A product designer who paints on weekends, collects playlists and always wins at chess.',
        photo: STOCK.partnerA,
        family: 'Daughter of Mr. Vikram & Mrs. Sunita Kapoor',
      },
    ],
  },
  story: {
    label: 'Our Story',
    title: 'How we got here',
    milestones: [
      {
        year: '2019',
        title: 'The first hello',
        description:
          'We met at a friend’s rooftop party and talked until the city went quiet.',
        image: STOCK.story[0],
      },
      {
        year: '2021',
        title: 'The big move',
        description:
          'A new city, a tiny apartment, one very loud plant — and a lot of laughter.',
        image: STOCK.story[1],
      },
      {
        year: '2025',
        title: 'The proposal',
        description:
          'On a misty morning in the mountains, a question — and the easiest yes ever.',
        image: STOCK.story[2],
      },
    ],
  },
  gallery: {
    label: 'Moments',
    title: 'A few of our favourite frames',
    images: [
      { src: STOCK.gallery[0], caption: 'The proposal', span: 'tall' },
      { src: STOCK.gallery[1], caption: 'Sunset walks', span: 'normal' },
      { src: STOCK.gallery[2], caption: 'Coffee mornings', span: 'normal' },
      { src: STOCK.gallery[3], caption: 'Festival of lights', span: 'wide' },
      { src: STOCK.gallery[4], caption: 'Mountain escape', span: 'normal' },
      { src: STOCK.gallery[5], caption: 'City nights', span: 'tall' },
    ],
  },
  video: {
    label: 'Pre-wedding Film',
    title: 'Our story, in motion',
    description:
      'Press play for a ninety-second glimpse of the journey that brought us here.',
    src: DEMO_VIDEO,
    poster: STOCK.videoPoster,
  },
  family: {
    label: 'The Families',
    title: 'With love, from both sides',
    groups: [
      {
        side: 'groom',
        title: 'Mehra Family',
        members: [
          { name: 'Mr. Rajiv Mehra', relation: 'Father of the Groom' },
          { name: 'Mrs. Anita Mehra', relation: 'Mother of the Groom' },
          { name: 'Ishaan Mehra', relation: 'Brother of the Groom' },
        ],
      },
      {
        side: 'bride',
        title: 'Kapoor Family',
        members: [
          { name: 'Mr. Vikram Kapoor', relation: 'Father of the Bride' },
          { name: 'Mrs. Sunita Kapoor', relation: 'Mother of the Bride' },
          { name: 'Riya Kapoor', relation: 'Sister of the Bride' },
        ],
      },
    ],
  },
  schedule: {
    label: 'The Celebration',
    title: 'Three days of togetherness',
    events: [
      {
        title: 'Engagement & Mehndi',
        date: 'Thursday, 12 February 2026',
        time: '6:00 PM onwards',
        venue: 'Courtyard Lawn, The Leela Palace',
        description:
          'An evening of henna, music and the first of many celebrations.',
        image: STOCK.events[0],
      },
      {
        title: 'The Wedding Ceremony',
        date: 'Saturday, 14 February 2026',
        time: '11:00 AM onwards',
        venue: 'Grand Pavilion, The Leela Palace',
        description:
          'The sacred vows, exchanged beneath a canopy of marigold and light.',
        image: STOCK.events[1],
      },
      {
        title: 'Reception Dinner',
        date: 'Saturday, 14 February 2026',
        time: '8:00 PM onwards',
        venue: 'Lakeview Terrace, The Leela Palace',
        description:
          'Dinner, dancing and a toast to the start of forever.',
        image: STOCK.events[2],
      },
    ],
  },
  venue: {
    label: 'Getting There',
    title: 'Where it all happens',
    name: 'The Leela Palace',
    address: 'Lake Pichola, Udaipur, Rajasthan 313001, India',
    mapQuery: 'The Leela Palace Udaipur',
  },
  rsvp: {
    label: 'Will you join us?',
    title: 'Kindly reply',
    description:
      'Your presence would mean the world to us. Please let us know by the date below.',
    deadline: '20 January 2026',
  },
  footer: {
    message: 'Thank you for being part of our story.',
    signature: 'Aarav & Diya',
    socials: [
      { label: 'Instagram', href: 'https://instagram.com' },
      { label: 'Facebook', href: 'https://facebook.com' },
      { label: 'WhatsApp', href: 'https://whatsapp.com' },
    ],
  },
};

// ── Resolver ───────────────────────────────────────────────────────────────

export function resolveContent(data: InvitationData): Dev21Content {
  const wedding = data.customData?.wedding ?? {};
  const [nameA, nameB] = splitNames(data.eventName);

  // Hero ---------------------------------------------------------------------
  const heroTitle = hasText(data.eventName)
    ? data.eventName
    : DEMO.hero.title;
  const heroBackground = data.mediaUrls?.[0] ?? DEMO.hero.background;

  // Couple -------------------------------------------------------------------
  const partners: Partner[] = [];
  const groom = wedding.groom;
  const bride = wedding.bride;
  if (groom || bride) {
    if (groom) {
      partners.push({
        name: groom.fullName || groom.nickname || 'Aarav',
        role: 'The Groom',
        about: groom.about ?? DEMO.couple.partners[0].about,
        photo: groom.photoUrl ?? DEMO.couple.partners[0].photo,
        family:
          hasText(groom.fatherName) || hasText(groom.motherName)
            ? `Son of ${[groom.fatherName, groom.motherName].filter(hasText).join(' & ')}`
            : DEMO.couple.partners[0].family,
      });
    }
    if (bride) {
      partners.push({
        name: bride.fullName || bride.nickname || 'Diya',
        role: 'The Bride',
        about: bride.about ?? DEMO.couple.partners[1].about,
        photo: bride.photoUrl ?? DEMO.couple.partners[1].photo,
        family:
          hasText(bride.fatherName) || hasText(bride.motherName)
            ? `Daughter of ${[bride.fatherName, bride.motherName].filter(hasText).join(' & ')}`
            : DEMO.couple.partners[1].family,
      });
    }
  }

  // Story --------------------------------------------------------------------
  const storyMoments = wedding.preWeddingStory?.moments?.filter((m) =>
    hasText(m.url),
  );
  const milestones: Milestone[] | undefined = storyMoments?.length
    ? storyMoments.map((m, i) => ({
        year: DEMO.story.milestones[i % DEMO.story.milestones.length].year,
        title: m.caption ?? `Chapter ${i + 1}`,
        description: m.description ?? '',
        image: m.url,
      }))
    : undefined;

  // Gallery ------------------------------------------------------------------
  const showcaseImages = (wedding.mediaShowcase ?? [])
    .filter((m) => m.type === 'image' && hasText(m.url))
    .map((m) => m.url);
  const galleryUrls = showcaseImages.length
    ? showcaseImages
    : (data.mediaUrls ?? []).slice(1);
  const SPANS: GalleryImage['span'][] = [
    'tall',
    'normal',
    'normal',
    'wide',
    'normal',
    'tall',
  ];
  const galleryImages: GalleryImage[] | undefined = galleryUrls.length
    ? galleryUrls.map((src, i) => ({ src, span: SPANS[i % SPANS.length] }))
    : undefined;

  // Video --------------------------------------------------------------------
  const videoItem = (wedding.mediaShowcase ?? []).find(
    (m) => m.type === 'video' && hasText(m.url),
  );

  // Family -------------------------------------------------------------------
  const familyGroups: FamilyGroup[] | undefined = wedding.familyGroups?.length
    ? wedding.familyGroups.map((g) => ({
        side: g.side,
        title: g.familyTitle ?? g.groupTitle,
        members: g.members
          .filter((m) => hasText(m.name) && hasText(m.relation))
          .map((m) => ({ name: m.name, relation: m.relation })),
      }))
    : undefined;

  // Schedule -----------------------------------------------------------------
  const scheduleEvents: ScheduleEvent[] | undefined = wedding.weddingEvents
    ?.length
    ? wedding.weddingEvents.map((e, i) => ({
        title: e.title,
        date: formatLong(e.date),
        time: [formatTime(e.startTime), formatTime(e.endTime)]
          .filter(Boolean)
          .join(' – '),
        venue: e.venue,
        description: e.description ?? '',
        image: e.coverImageUrl ?? STOCK.events[i % STOCK.events.length],
      }))
    : undefined;

  return {
    announcement: DEMO.announcement,
    hero: {
      kicker: DEMO.hero.kicker,
      title: heroTitle,
      subtitle: DEMO.hero.subtitle,
      host: hasText(data.hostName) ? data.hostName : DEMO.hero.host,
      dateLabel: hasText(data.eventDate)
        ? formatLong(data.eventDate)
        : DEMO.hero.dateLabel,
      venueShort: hasText(data.eventVenue)
        ? data.eventVenue
        : DEMO.hero.venueShort,
      background: heroBackground,
    },
    couple: {
      ...DEMO.couple,
      partners: partners.length === 2 ? partners : DEMO.couple.partners,
    },
    story: {
      ...DEMO.story,
      milestones: milestones ?? DEMO.story.milestones,
    },
    gallery: {
      ...DEMO.gallery,
      images: galleryImages ?? DEMO.gallery.images,
    },
    video: {
      ...DEMO.video,
      src: videoItem?.url ?? DEMO.video.src,
      poster: data.mediaUrls?.[0] ?? DEMO.video.poster,
    },
    family: {
      ...DEMO.family,
      groups: familyGroups ?? DEMO.family.groups,
    },
    schedule: {
      ...DEMO.schedule,
      events: scheduleEvents ?? DEMO.schedule.events,
    },
    venue: {
      ...DEMO.venue,
      name: hasText(data.eventVenue) ? data.eventVenue : DEMO.venue.name,
      address: hasText(data.eventVenue) ? data.eventVenue : DEMO.venue.address,
      mapQuery: hasText(data.eventVenue)
        ? data.eventVenue
        : DEMO.venue.mapQuery,
    },
    rsvp: {
      ...DEMO.rsvp,
      description: hasText(data.customMessage)
        ? data.customMessage
        : DEMO.rsvp.description,
      externalLink: hasText(data.rsvpLink) ? data.rsvpLink : undefined,
    },
    footer: {
      ...DEMO.footer,
      signature: hasText(data.eventName)
        ? data.eventName
        : DEMO.footer.signature,
    },
  };
}
